/**
 * StarterMap.ts
 *
 * THE base template map for Helix 2000.
 *
 * This is the ONE built-in map. All other levels are created via the Editor
 * and stored dynamically via LevelService.
 *
 * Philosophy:
 * - Clean, minimal slate
 * - Single ground platform for immediate playability
 * - Empty tile layers ready for editor painting
 * - No auto-converted tiles (explicit platforms only)
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

// =============================================================================
// CONFIGURATION (Uses MAP_DEFAULTS from config/map/defaults.config.ts)
// =============================================================================

const MAP_ID = 'starter-map';
const MAP_NAME = 'Starter Map';
const VERSION = '1.0';

const W = MAP_DEFAULTS.widthTiles;
const H = MAP_DEFAULTS.heightTiles;
const T = MAP_DEFAULTS.tileSize;

// =============================================================================
// LAYERS (SPARSE STORAGE - All Empty, Ready for Editor)
// =============================================================================

const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

// =============================================================================
// ENTITIES (None - Add via Editor)
// =============================================================================

const entities: any[] = [];

// =============================================================================
// SPAWN POINTS (Uses MAP_DEFAULTS)
// =============================================================================

const spawnPoints = [
  { x: MAP_DEFAULTS.spawnPoint.x, y: MAP_DEFAULTS.spawnPoint.y }
];

const defaultSpawn = {
  x: MAP_DEFAULTS.spawnPoint.x * T,
  y: MAP_DEFAULTS.spawnPoint.y * T
};

// =============================================================================
// PARALLAX (None - Add if needed)
// =============================================================================


// =============================================================================
// RUNTIME PLATFORMS (Uses MAP_DEFAULTS)
// =============================================================================

/**
 * Single ground platform spanning full width.
 * Ground moved to y: 500 (was 800) - player was spawning in air and falling.
 * This is the ONLY platform - everything else is added via Editor.
 */
const runtimePlatforms = [
  {
    id: 'ground-floor',
    x: 0,
    y: MAP_DEFAULTS.ground.y,  // Now 500 from config
    width: W * T,
    height: MAP_DEFAULTS.ground.height,
    type: MAP_DEFAULTS.ground.type,
    color: MAP_DEFAULTS.ground.color
  }
];

// =============================================================================
// EXPORT
// =============================================================================

export const StarterMap: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: MAP_DEFAULTS.backgroundColor,
  gravity: MAP_DEFAULTS.gravity,

  spawnPoints,
  defaultSpawn,

  layers: [
    {
      name: LayerType.GROUND,
      visible: true,
      locked: false,
      opacity: 1.0,
      tiles: groundLayer
    },
    {
      name: LayerType.COLLISION,
      visible: true,
      locked: false,
      opacity: 1.0,
      tiles: collisionLayer
    },
    {
      name: LayerType.OBJECTS,
      visible: true,
      locked: false,
      opacity: 1.0,
      tiles: objectsLayer
    },
    {
      name: LayerType.ENTITIES,
      visible: true,
      locked: false,
      opacity: 1.0,
      tiles: entitiesLayer,
      entities
    },
    {
      name: LayerType.TRIGGERS,
      visible: true,
      locked: false,
      opacity: 1.0,
      tiles: triggersLayer
    }
  ],

  platforms: runtimePlatforms,
  spawnRules: [],
  type: 'town',
  metadata: {
    author: 'PPC',
    description: 'The standard template for all Helix 2000 worlds. A clean slate featuring a single persistent ground layer and standard physics settings.',
    difficulty: 'Low',
    maxPlayers: 1,
    version: '1.0',
    tags: ['template', 'town', 'starting-map'],
    // PvE-default profile — standard open-world rules. PvP off,
    // friendlyFire off, 3s respawn, 2.5s post-respawn invuln, no
    // death penalty. Mob populations come from
    // `content/zones/starter_map.toml` via the zone-spawner pipeline.
    profile: 'pve_default',
  }
};

// Export helpers for level creation
export { W, H, T };
